/* eslint max-lines-per-function: ["error", { max: 203 }] -- applies to entire file */
/* eslint max-statements: ["error", { max: 27 }] -- applies to entire file */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import isEmpty from "lodash/isEmpty";

import {
  AnalyticsComponent,
  SMTAnalyticsPageView,
  SMTAnalyticsSource,
  SMTEntryAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { ModalPath } from "@/constants/Navigation";
import { SMTModalSubDeepLinks } from "@/constants/SMT";
import { SMTTestID } from "@/constants/TestID";
import useJournalEntrySubDeepLink from "@/hooks/deepLinking/subDeepLinks/useJournalEntrySubDeepLink";
import useSubDeepLink from "@/hooks/deepLinking/useSubDeepLink";
import useAppUserSMTPermissions from "@/hooks/useAppUserSMTPermissions";
import { useModalTracker } from "@/hooks/useModalTracker";
import { useAnalytics } from "@/services/Analytics";
import { useGetSMTDailyLogsInfiniteQuery } from "@/store/api/smt";
import { useGetFullProfileQuery } from "@/store/api/user";
import { useAppUserSelector } from "@/store/reducers/appUser";
import AddSMTEntryFlow from "@/ui/components/smt/AddSMTEntry/AddSMTEntryFlow";
import AllSMTDailyEntriesModal from "@/ui/components/smt/ViewSMTEntries/AllSMTDailyEntriesModal";
import ViewSMTEntryBottomSheetModal from "@/ui/components/smt/ViewSMTEntry/ViewSMTEntryBottomSheetModal";
import { isWeb } from "@/utils/platform.util";
import {
  getInitialDateRangeForAllSMTDailyLogs,
  selectAccordionPropsList,
} from "@/utils/smt.util";
import { getTabSelectorOptions } from "@/utils/user.util";

const ThrottleFetchRequestsDelayMs = 750;

const ViewAllModalPath = ModalPath.Journal;

const baseTestID = SMTTestID.ViewAllSMTDailyEntriesModal;

const Modal = () => {
  const { setActiveModal } = useModalTracker();
  useEffect(() => {
    setActiveModal(ViewAllModalPath);
    return () => {
      setActiveModal(null);
    };
  }, [setActiveModal]);

  const { trackPageView } = useAnalytics();
  useEffect(() => {
    trackPageView(SMTAnalyticsPageView.ViewAllSMTDailyEntriesModal);
  }, [trackPageView]);

  const { externalId: appUserId, isPatient: isAppUserPatient } =
    useAppUserSelector();
  const { data: fullProfileData, isLoading: isLoadingFullProfileData } =
    useGetFullProfileQuery(undefined, {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      skip: !isAppUserPatient,
    });
  const {
    shouldAppUserSeeAddSMTEntriesButton,
    supportPatientsEligibleForSMT: { supportPatientsWithSMTViewAccess },
  } = useAppUserSMTPermissions();
  const userTabs = useMemo(() => {
    return (
      getTabSelectorOptions({
        appUserCount: 0,
        appUserId,
        isAppUserPatient,
        patients: supportPatientsWithSMTViewAccess,
        shouldIncludeSelf: false,
      }) || []
    );
  }, [appUserId, isAppUserPatient, supportPatientsWithSMTViewAccess]);

  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(
    undefined,
  );

  const [shouldOpenAddEntry, setShouldOpenAddEntry] = useState<boolean>(false);
  const [isViewEntryModalOpen, setIsViewEntryModalOpen] = useState(false);

  const handleOpenEntry = () => {
    setIsViewEntryModalOpen(true);
  };

  const handleCloseEntry = () => {
    setIsViewEntryModalOpen(false);
  };

  const handleAddEntry = useCallback(() => {
    setShouldOpenAddEntry(true);
  }, []);

  useSubDeepLink({
    analyticsEvent: SMTEntryAnalyticsEvent.AddEntryOpenedFromDeepLink,
    analyticsProps: {
      component: AnalyticsComponent.FloatingButton,
      source: SMTAnalyticsSource.ViewAllSMTDailyEntriesModal,
    },
    onOpen: handleAddEntry,
    // on mobile, custom deeplinking logic means target is journal modal, subtarget is trigger 'add' modal
    // on web, no custom deeplinking logic, so target is just trigger 'add' modal
    paramName: isWeb() ? "target" : "subtarget",
    shouldHandle: shouldAppUserSeeAddSMTEntriesButton,
    targetValue: SMTModalSubDeepLinks.Add,
  });

  const { entry } = useJournalEntrySubDeepLink({
    onOpen: handleOpenEntry,
  });

  useEffect(() => {
    if (!isEmpty(selectedUserId)) return;
    if (isAppUserPatient) {
      setSelectedUserId(appUserId);
      return;
    }
    if (isEmpty(userTabs)) return;
    setSelectedUserId(userTabs[0].userId);
  }, [appUserId, isAppUserPatient, selectedUserId, userTabs]);

  const admitDate = useMemo(() => {
    const selectedPatient = isAppUserPatient
      ? fullProfileData
      : supportPatientsWithSMTViewAccess.find(
          (p) => p.externalId === selectedUserId,
        );
    return selectedPatient?.admitDate;
  }, [
    isAppUserPatient,
    fullProfileData,
    supportPatientsWithSMTViewAccess,
    selectedUserId,
  ]);

  const {
    accordionPropsList,
    fetchNextPage,
    hasNextPage,
    isFetching: isApiFetching,
    isFetchingNextPage: isApiFetchingNextPage,
    refetch: refetchAllDailyLogs,
  } = useGetSMTDailyLogsInfiniteQuery(
    {
      admitDate,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- the skip guarantees this is not undefined
      patientId: selectedUserId!,
    },
    {
      // initialPageParam has a default value in store/api/smt.ts, however it can also be overridden here explicitly
      // by providing initialPageParam: { endDate, startDate } to force that to be the args for the first fetched page
      initialPageParam: getInitialDateRangeForAllSMTDailyLogs({
        admitDate,
      }),
      selectFromResult: (result) => ({
        accordionPropsList: selectAccordionPropsList(result),
        hasNextPage: result.hasNextPage,
        isFetching: result.isFetching,
        isFetchingNextPage: result.isFetchingNextPage,
      }),
      // eslint-disable-next-line @typescript-eslint/naming-convention -- skip is a valid option
      skip:
        isEmpty(selectedUserId) ||
        (isAppUserPatient && isLoadingFullProfileData),
    },
  );

  // a refresh forces a full refetch of all pages, not just getting the next page
  const isRefreshing = useMemo(
    () => isApiFetching && !isApiFetchingNextPage,
    [isApiFetching, isApiFetchingNextPage],
  );

  // prevent multiple concurrent fetch requests
  const [isFetchPending, setIsFetchPending] = useState<boolean>(false);
  useEffect(() => {
    if (isApiFetching) return;
    // once isFetchPending transitions to false, reflect it in shouldShowFetchingSpinner
    // setting this to true is only done manually in handleEndReached
    setIsFetchPending(false);
  }, [isApiFetching]);

  const isFetchPendingRef = useRef<boolean>(isFetchPending);
  useEffect(() => {
    isFetchPendingRef.current = isFetchPending;
  }, [isFetchPending]);

  const handleEndReached = useCallback(() => {
    if (!hasNextPage) return;

    // prevent multiple concurrent fetch requests
    if (isFetchPendingRef.current) return;

    // since we delay actually firing the next request,
    // make sure the spinner is shown even when we're waiting on that delay
    setIsFetchPending(true);

    // throttle fetch requests so that a list with only 1 or 2 items
    // doesn't immediately start sending a bunch of requests one after another
    const timeoutId = setTimeout(() => {
      void fetchNextPage();
    }, ThrottleFetchRequestsDelayMs);

    // clear timeout / cancel upcoming request if the entire modal component unmounts
    return () => clearTimeout(timeoutId);
  }, [hasNextPage, fetchNextPage]);

  const handleRefresh = useCallback(() => {
    void refetchAllDailyLogs();
  }, [refetchAllDailyLogs]);

  return (
    <>
      <AllSMTDailyEntriesModal
        accordionPropsList={accordionPropsList}
        isFetchingNextPage={isFetchPending}
        isRefreshing={isRefreshing}
        onEndReached={handleEndReached}
        onRefresh={handleRefresh}
        onUserChange={(userId: string) => setSelectedUserId(userId)}
        testID={baseTestID}
        userTabs={userTabs}
      />
      {shouldAppUserSeeAddSMTEntriesButton && (
        <AddSMTEntryFlow
          screenPath={ViewAllModalPath}
          shouldOpenImmediately={shouldOpenAddEntry}
        />
      )}
      {entry && (
        <ViewSMTEntryBottomSheetModal
          entry={entry}
          isOpen={isViewEntryModalOpen}
          onDismiss={handleCloseEntry}
          onOpen={handleOpenEntry}
        />
      )}
    </>
  );
};

export default Modal;
